import { MySupportHistoryControllerService } from '@/__generated__/FrontApi';
import Button from '@/components/Button';
import Container from '@/components/Container';
import { H3, H3_2 } from '@/components/Titles';
import { breakpoint } from '@/helpers/BreakpointHelper';
import { DonationSummaryData } from '@/helpers/SupportHelper';
import LayoutWithTitle from '@/layouts/LayoutWithTitle';
import {
  ContainerGroup,
  SectionTop,
  TitleArea,
} from '@/page-blocks/mypage/MypageCommon';
import MypageRoutes from '@/page-blocks/mypage/MypageRoutes';
import MyPageSupportHistoryTab1 from '@/page-blocks/mypage/support-history/MyPageSupportHistoryTab1';
import MyPageSupportHistoryTab2 from '@/page-blocks/mypage/support-history/MyPageSupportHistoryTab2';
import MyPageSupportHistoryTab3 from '@/page-blocks/mypage/support-history/MyPageSupportHistoryTab3';
import SupportHistoryTab from '@/page-blocks/mypage/SupportHistoryTab';
import { useUserStore } from '@/stores/UserStore';
import { useLocation } from '@reach/router';
import { navigate } from 'gatsby';
import { observer } from 'mobx-react-lite';
import React, { FC, useMemo, useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import SwiperCore from 'swiper';

const UserName = styled.span`
  font-size: 32px;
  font-weight: bold;
  color: #1cabe2;
  ${breakpoint(640)} {
    font-size: 20px;
  }
`;
const ColorSky = styled.span`
  color: #1cabe2;
`;

const ButtonContainer = styled.div`
  width: 352px;
  display: flex;
  justify-content: space-between;
  ${breakpoint(1024)} {
    position: relative;
  }
  ${breakpoint(640)} {
    width: 100%;
    ${Button} {
      position: static;
      width: 49%;
    }
  }
`;
const NoData = styled.div`
  text-align: center;
  border-top: 1px solid #e5e6e8;
  border-bottom: 1px solid #e5e6e8;
  padding: 96px 0;
  ${ButtonContainer} {
    width: 256px;
    margin: 16px auto 0;
  }
  ${H3} {
    line-height: 1.7;
  }
  ${breakpoint(640)} {
    ${H3} {
      font-size: 14px;
      line-height: 28px;
    }
    ${ButtonContainer} {
      ${Button} {
        width: 100%;
      }
    }
  }
`;

const MissSupportHistory: FC = observer(() => {
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [
    location.search,
  ]);
  const userStore = useUserStore();
  const [activeTab, setActiveTab] = useState<number>(() =>
    params.get('tabIndex') ? Number(params.get('tabIndex')) : 0,
  );

  const [hasBgfLanding, setHasBgfLanding] = useState<boolean>(false);
  useEffect(() => {
    setHasBgfLanding(!!sessionStorage.getItem('bgfLanding'));
  }, []);

  // 탭 배열을 component 필드와 함께 구성 -> 인덱스 하나만 보면 됨
  const tabs = useMemo(() => {
    const base = [
      {
        title: '완료 후원',
        link: '/mypage/support-history/',
        component: <MyPageSupportHistoryTab1 />,
      },
      {
        title: '놓친 후원',
        link: '/mypage/miss-support-history/',
        component: <MyPageSupportHistoryTab2 params={params} />,
      },
    ];

    if (hasBgfLanding) {
      // CU 탭을 맨 앞에 추가
      return [
        {
          title: 'CU 착한기부 후원',
          link: '/mypage/support-history/',
          component: <MyPageSupportHistoryTab3 />,
        },
        ...base,
      ];
    }
    return base;
  }, [hasBgfLanding, params]);

  // 화면 너비에 따른 slidesPerView 관리 (즉시 반응)
  const [slidesPerView, setSlidesPerView] = useState<number | number[]>(
    tabs.length,
  );
  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      if (w <= 400) setSlidesPerView(2);
      else setSlidesPerView(tabs.length);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [tabs.length]);

  // Location이 바뀌면(뒤로가기 등) 쿼리 tabIndex가 있으면 activeTab 동기화
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    const t = q.get('tabIndex');
    const num = Number(t);

    if (t !== null && !Number.isNaN(num) && num >= 0 && num < tabs.length) {
      setActiveTab(num);
    } else if (activeTab >= tabs.length) {
      setActiveTab(0);
    }
  }, [activeTab, location.search, tabs.length]);

  const [swiperInstance, setSwiperInstance] = useState<SwiperCore | null>(null);

  // 사용자 정기후원 정보
  const [donationSummary, setDonationSummary] = useState<DonationSummaryData>();
  const loadUserInfo = useCallback(async () => {
    const {
      data,
      resultCode,
    } = await MySupportHistoryControllerService.getMyCompleteSummaryUsingPost({
      pathType: `SUPPORT`,
    });
    if (resultCode === `success`) {
      setDonationSummary(data as DonationSummaryData);
    }
  }, [setDonationSummary]);
  useEffect(() => {
    loadUserInfo();
  }, []);

  return (
    <LayoutWithTitle
      location={location}
      title="마이페이지"
      description="for every child, UNICEF"
    >
      <MypageRoutes currentKey={3} />
      <SectionTop>
        <ContainerGroup>
          {donationSummary && (
            <Container>
              <TitleArea>
                <H3_2>
                  <UserName>{userStore?.user?.name}</UserName>
                  {` `}후원자님, <br />
                  {donationSummary.firstDonateDate}부터 총{` `}
                  <ColorSky>
                    {Number(donationSummary.accumTotalAmount).toLocaleString()}
                  </ColorSky>
                  원 후원하셨습니다.
                </H3_2>
              </TitleArea>
            </Container>
          )}
          <Container>
            <SupportHistoryTab
              tabs={tabs}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              navigate={navigate}
              slidesPerView={slidesPerView}
              swiperInstance={swiperInstance}
              setSwiperInstance={setSwiperInstance}
              location={location}
            />
            {userStore.isDonor() || userStore.isRegularDonor() ? (
              <>
                {/* CU 착한기부 */}
                {hasBgfLanding && activeTab === 0 && (
                  <MyPageSupportHistoryTab3 />
                )}

                {/* 완료 후원 */}
                {(hasBgfLanding ? activeTab === 1 : activeTab === 0) && (
                  <MyPageSupportHistoryTab1 />
                )}

                {/* 놓친 후원 */}
                {(hasBgfLanding ? activeTab === 2 : activeTab === 1) && (
                  <MyPageSupportHistoryTab2 params={params} />
                )}
              </>
            ) : (
              <NoData>
                <H3>
                  유니세프에 후원 중이신가요? <br />
                  신청할 후원내역이 확인되지 않습니다. <br />
                  회원정보와 후원내역을 연결 후 후원 내역을 관리할 수 있습니다.
                </H3>
                <ButtonContainer>
                  <Button
                    full
                    onClick={() => navigate(`/mypage/support-history/connect`)}
                  >
                    후원내역 연결하기
                  </Button>
                </ButtonContainer>
              </NoData>
            )}
          </Container>
        </ContainerGroup>
      </SectionTop>
    </LayoutWithTitle>
  );
});
export default MissSupportHistory;
